import type { Manuscript, Task } from "@/lib/types";

export interface ManuscriptSection {
  id: string;
  noteId: string;
  title: string;
  encTitle?: string | null;
  encryptionScopeId?: string | null;
  encrypted?: boolean;
  contentEpoch?: number;
  position: number;
  /** "abstract" (first, one per manuscript) or "section". */
  role?: "abstract" | "section";
  deletedAt: string | null;
  workspaceId: string;
}

export interface StatusHistoryRow {
  toValue: string | null;
  fromValue: string | null;
  effectiveDate: string | null;
  reason: string | null;
  changedAt: string;
  by: string | null;
}

export interface SubmissionSummary {
  id: string;
  venue: string;
  submittedOn: string;
  identifiers: string | null;
  snapshotSha256: string;
  createdAt: string;
}

export interface CommentSetSummary {
  id: string;
  reviewerLabel: string;
  receivedOn: string | null;
  submissionId: string | null;
  createdAt: string;
  /** "human" (received from a venue) or "ai" (an AI review round). */
  source?: "human" | "ai";
  round?: number | null;
  review?: AiReview | null;
}

export interface AiReview {
  scores: { overall: number; soundness: number; evidence: number; clarity: number; contribution: number };
  verdict: string;
  summary: string;
  strengths: string[];
  /** The method used (the skill's name). */
  method: string;
  model: string | null;
  isDemo?: boolean;
  /** Overall 8/10 or more and no major problem that revising could fix. */
  ready: boolean;
}

export interface ManuscriptDetail {
  manuscript: Manuscript;
  sections: ManuscriptSection[];
  history: StatusHistoryRow[];
  submissions: SubmissionSummary[];
  commentSets: CommentSetSummary[];
  tasks: Task[];
  latestVersion?: { id: string; number: number; label: string; createdAt: string } | null;
}

export interface CheckIssue {
  id: string;
  category: string;
  message: string;
  noteId?: string;
  sectionTitle?: string;
  line?: number;
}

export interface CheckResult {
  issues: CheckIssue[];
  sectionCount: number;
  citedCount: number;
  checkedAt: string;
  disclaimer: string;
}

export interface RevisionRow {
  id: string;
  number: string;
  position: number;
  commentSetId: string;
  requestText: string;
  sectionId: string | null;
  /** Why the app placed it in that section; null when a person chose. */
  sectionHint?: string | null;
  proposedChange: string;
  changeStatus: "pending" | "confirmed" | "declined";
  confirmedChange: string | null;
  confirmedAt: string | null;
  confirmedByName: string | null;
  responseDraft: string;
  taskId: string | null;
  version: number;
  reviewerLabel: string;
  section: { id: string; noteId: string; title: string } | null;
  task: { id: string; title: string; status: string } | null;
  responseClaimsUnconfirmedChange: boolean;
  severity?: string | null;
  category?: string | null;
  persona?: string | null;
  quote?: string | null;
}

export const ISSUE_CATEGORY_LABELS: Record<string, string> = {
  unresolved_citation: "Citations not found in the library",
  missing_metadata: "Missing paper details",
  placeholder: "Placeholders in the text",
  evidence_state: "Evidence that is not recorded as checked",
  source_changed: "Sources changed since they were cited",
  claim_changed: "Claims changed since they were linked to evidence",
  unconfirmed_revision: "Responses that claim unconfirmed changes",
  pending_revision: "Reviewer requests still pending",
  empty_section: "Empty sections",
};

export const ISSUE_CATEGORY_ORDER = Object.keys(ISSUE_CATEGORY_LABELS);

export function groupIssues(issues: CheckIssue[]) {
  const groups = new Map<string, CheckIssue[]>();
  for (const i of issues) groups.set(i.category, [...(groups.get(i.category) ?? []), i]);
  return [...groups.entries()].sort((a, b) => ISSUE_CATEGORY_ORDER.indexOf(a[0]) - ISSUE_CATEGORY_ORDER.indexOf(b[0]));
}

export interface ImportPreview {
  hash: string;
  format: "latex" | "docx" | "odt" | "markdown";
  source: string;
  otherFiles: string[];
  title: { imported: string | null; current: string };
  sections: {
    index: number;
    title: string;
    role: "abstract" | "section";
    words: number;
    citations: number;
    text: string;
    note: string | null;
    suggestion: { action: "update" | "new" | "skip"; sectionId: string | null; reason: string | null };
    unchanged: boolean;
    /** Links to notes, tasks or files in the current text that the file does not have. */
    lostLinks: number;
  }[];
  existing: { id: string; title: string; role: string; encrypted: boolean; words: number; matched: boolean; text: string | null }[];
  statements: { field: string; title: string; text: string; current: string | null; changed: boolean }[];
  citations: { total: number; inLibrary: number; toAdd: { key: string; title: string; year: number | null; from?: "bib" | "word" }[]; unknown: string[] };
  warnings: string[];
}

export interface ManuscriptVersion {
  id: string;
  number: number;
  label: string;
  source: "manual" | "import" | "before-import" | "submission" | "ai-review" | "restore" | "before-restore" | "before-agent" | "git";
  createdAt: string;
  by: string | null;
  sectionCount: number;
  words: number;
  sections: { sectionId: string; title: string; role: string; words: number; encrypted: boolean }[];
  files: { index: number; filename: string; sizeBytes: number; sha256: string }[];
  details: { counts?: { updated: number; added: number; removed: number; unchanged: number }; createdPapers?: { id: string; citationKey: string | null; title: string }[]; warnings?: string[]; format?: string };
}

export interface LogEvent {
  id: string;
  at: string;
  kind: "created" | "version" | "edit" | "status" | "submission" | "review" | "response" | "comment";
  title: string;
  detail?: string | null;
  by?: string | null;
  sectionId?: string | null;
  versionNumber?: number | null;
  count?: number;
  from?: string;
}

export interface ManuscriptLog {
  summary: {
    version: { number: number; label: string; source: string; createdAt: string; by: string | null } | null;
    changedSinceVersion: boolean | null;
    lastEdit: { at: string | null; by: string | null; sectionId: string; sectionTitle: string | null } | null;
    status: { value: string; label: string; since: string | null };
    submission: { venue: string; submittedOn: string; versionNumber: number | null; count: number } | null;
    reviews: { sets: number; aiRounds: number; open: number; total: number };
  };
  sections: { sectionId: string; title: string | null; encrypted: boolean; words: number | null; lastEditedAt: string | null; lastEditedBy: string | null; changedSinceVersion: boolean | null }[];
  events: LogEvent[];
}

export const mqk = {
  detail: (id: string) => ["manuscript", id] as const,
  versions: (id: string) => ["manuscript-versions", id] as const,
  log: (id: string) => ["manuscript-log", id] as const,
  check: (id: string) => ["manuscript-check", id] as const,
  revisions: (id: string) => ["revisions", id] as const,
  responseDraft: (id: string) => ["response-draft", id] as const,
};
